import { FC, useEffect } from 'react';
import { ArrowRightIcon, ExternalLinkIcon } from 'assets/icons';
import { LoadingSpinnerAnimation } from 'assets/animations';
import Lottie from 'lottie-react';
import { VoidFn } from 'types/baseTypes';
import { PayoutData } from '../payouts.model';
import { useLazyGetPayoutReceiptDisclosureQuery } from '../api';
import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import { openInNewTab } from 'utils/commonUtils';
import { GenericError } from 'components';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';

const ET_TIMEZONE = 'America/New_York';

interface PayoutReceiptPanelProps {
  onClose: VoidFn;
  payoutData: PayoutData;
}

const PayoutReceiptPanel: FC<PayoutReceiptPanelProps> = ({ onClose, payoutData }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [getPayoutReceiptDisclosure, { data: disclosure, isFetching, isError, isSuccess, isUninitialized }] =
    useLazyGetPayoutReceiptDisclosureQuery();

  useEffect(() => {
    if (currentTeam?.id && payoutData.ID) {
      getPayoutReceiptDisclosure({ teamId: currentTeam.id, payoutId: payoutData.ID });
    }
  }, [getPayoutReceiptDisclosure, currentTeam?.id, payoutData.ID]);

  const handleDisclosureLinkClick = () => {
    if (disclosure?.url) {
      openInNewTab(disclosure.url);
    }
  };

  const getReceiptDate = () => {
    const date = new Date(payoutData.createdAt);
    const formattedDate = DateUtils.getDateInFormatWtTimeZone({
      date,
      timeZone: ET_TIMEZONE,
      dateFormat: 'dd MMM yyyy'
    });
    const tzAbbreviation = DateUtils.getTimeZoneAbbreviationForDate(date, ET_TIMEZONE);
    return tzAbbreviation ? `${formattedDate} (${tzAbbreviation})` : formattedDate;
  };

  const renderInfoRow = (label: string, value: string) => (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="shrink-0 text-17px text-primaryText">{label}</div>
      <div className="text-right text-17px text-headingGray">{value}</div>
    </div>
  );

  const amount = getAmountWithCurrency(+payoutData.amount);
  const businessName = payoutData.businessName || '';
  const disclosureBody = [disclosure?.text, disclosure?.companyAddress].filter(Boolean).join(' ').trim();

  const showLoading = isFetching || isUninitialized;
  const showError = !showLoading && (isError || !isSuccess);

  return (
    <div className="flex h-full w-96 flex-col">
      <div
        className="flex shrink-0 cursor-pointer items-center gap-3 border-b border-borderGray px-3.5 pb-4 pt-6"
        onClick={onClose}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Back to payout details">
        <ArrowRightIcon className="rotate-180" />
        <div className="text-17px font-semibold text-headingGray">Payout receipt</div>
      </div>

      {showLoading && (
        <div className="flex min-h-0 w-full flex-1 items-center justify-center">
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </div>
      )}

      {showError && (
        <div className="flex min-h-0 w-full flex-1 items-center justify-center px-6">
          <GenericError handleErrorBack={onClose} />
        </div>
      )}

      {!showLoading && !showError && disclosure && (
        <div className="customNormalScroll flex min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden pb-9">
          <div className="bg-secondaryBtn px-7 py-5">
            <div className="text-xl font-semibold text-primaryBtn" aria-label="ScanPay">
              ScanPay
            </div>
          </div>

          <div className="space-y-2 px-7 py-6">
            {renderInfoRow('Date', getReceiptDate())}
            {renderInfoRow('Payout ID', payoutData.ID)}
            {renderInfoRow('Business name', businessName)}
          </div>

          <div className="px-7">
            <div className="pb-3 text-sbase text-accentText">Summary</div>
            <div className="rounded-lg border border-borderGray px-4 py-3">
              <div className="flex items-start justify-between gap-3 pb-3">
                <div className="text-17px text-primaryText">Amount paid out from {businessName}</div>
                <div className="shrink-0 text-17px text-headingGray">{amount}</div>
              </div>
              <div className="border-b border-borderGray" />
              <div className="flex items-start justify-between gap-3 pt-3">
                <div className="text-17px font-semibold text-headingGray">Total</div>
                <div className="shrink-0 text-17px font-semibold text-headingGray">{amount}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-4 px-7">
            {disclosureBody && <p className="text-sbase leading-5 text-primaryText">{disclosureBody}</p>}
            {disclosure.contactPhone && (
              <p className="text-sbase leading-5 text-primaryText">
                Contact us by telephone at {disclosure.contactPhone}
              </p>
            )}
            {disclosure.url && (
              <button
                type="button"
                className="flex cursor-pointer items-center gap-2 text-left text-17px font-semibold text-secondary"
                onClick={handleDisclosureLinkClick}
                aria-label="View consumer disclosure and rights notice">
                <ExternalLinkIcon className="path-fill-current h-4 w-4 shrink-0 text-secondary" aria-hidden />
                View consumer disclosure and rights notice
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutReceiptPanel;
